// Topic-level settings (name, category, description, order, safety-screen
// requirement, owner guide). Not versioned — changes apply immediately.

import { useState } from "react";
import { errorText, updateTopic } from "../../services/talkTracks";
import { AURA_GUIDE_ID } from "../../types/guide";
import type { CharacterProfile } from "../../types/guide";
import type { TalkTrackTopic } from "../../types/talkTrack";

export function TopicSettings({
  topic,
  categories,
  guides,
  onSaved,
  onDelete,
}: {
  topic: TalkTrackTopic;
  categories: string[];
  // Candidate owners (every guide but Aura; drafts own once published).
  guides: CharacterProfile[];
  onSaved: (topic: TalkTrackTopic) => void;
  // Only offered for never-published topics (the server enforces it too).
  onDelete?: () => void;
}) {
  // The page keys this component by topic.updatedAt, so a reload resets it.
  const [name, setName] = useState(topic.name);
  const [category, setCategory] = useState(topic.category);
  const [description, setDescription] = useState(topic.description);
  const [sortOrder, setSortOrder] = useState(topic.sortOrder);
  const [requiresSafetyScreen, setRequiresSafetyScreen] = useState(topic.requiresSafetyScreen);
  const [ownerGuideId, setOwnerGuideId] = useState(topic.ownerGuideId ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const changed =
    name !== topic.name ||
    category !== topic.category ||
    description !== topic.description ||
    sortOrder !== topic.sortOrder ||
    requiresSafetyScreen !== topic.requiresSafetyScreen ||
    ownerGuideId !== (topic.ownerGuideId ?? "");

  async function save() {
    setBusy(true);
    setError(null);
    try {
      onSaved(await updateTopic(topic.id, {
        name: name.trim(), category, description, sortOrder, requiresSafetyScreen,
        ownerGuideId: ownerGuideId || null,
      }));
    } catch (e) {
      setError(`Save failed: ${errorText(e)}`);
    } finally {
      setBusy(false);
    }
  }

  const options = categories.includes(category) ? categories : [...categories, category];
  const owners = guides.filter((g) => g.id !== AURA_GUIDE_ID);
  const ownerKnown = !ownerGuideId || owners.some((g) => g.id === ownerGuideId);

  return (
    <details className="tt-details tt-settings">
      <summary>Topic settings</summary>
      <div className="row-3">
        <div>
          <label htmlFor="tt-set-name">Name</label>
          <input id="tt-set-name" type="text" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label htmlFor="tt-set-category">Category</label>
          <select id="tt-set-category" value={category} onChange={(e) => setCategory(e.target.value)}>
            {options.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="tt-set-order">Sort order</label>
          <input id="tt-set-order" type="number" value={sortOrder}
            onChange={(e) => setSortOrder(parseInt(e.target.value || "0", 10))} />
        </div>
      </div>
      <label htmlFor="tt-set-owner">Owner guide (hand-overs on this topic go to it)</label>
      <select id="tt-set-owner" value={ownerGuideId} onChange={(e) => setOwnerGuideId(e.target.value)}>
        <option value="">None — Aura keeps the conversation</option>
        {owners.map((g) => (
          <option key={g.id} value={g.id}>
            {g.name} ({g.id}){g.status !== "published" ? ` · ${g.status}, offered once published` : ""}
          </option>
        ))}
        {!ownerKnown && <option value={ownerGuideId}>{ownerGuideId}</option>}
      </select>
      <label htmlFor="tt-set-description">Description</label>
      <textarea id="tt-set-description" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
      <label className="tt-toggle">
        <input type="checkbox" checked={requiresSafetyScreen}
          onChange={(e) => setRequiresSafetyScreen(e.target.checked)} />
        Requires at least one safety screen before publishing
      </label>
      <div className="tt-actions">
        <button className="btn btn-sm btn-primary" onClick={save} disabled={busy || !changed || !name.trim()}>
          {busy ? "Saving…" : "Save settings"}
        </button>
        {error && <span className="error-text">{error}</span>}
        {onDelete && (
          <button className="btn btn-sm btn-danger" onClick={onDelete} style={{ marginLeft: "auto" }}>
            Delete topic
          </button>
        )}
      </div>
    </details>
  );
}
